import React from 'react';
import { 
  LinearProgress, 
  Box, 
  Typography, 
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { 
  CloudUpload, 
  AutoAwesome, 
  Check, 
  Error as ErrorIcon,
  Refresh
} from '@mui/icons-material';
import { ProcessingStatusResponse } from '../../services/document';

interface ProcessingStatusProps {
  status: ProcessingStatusResponse;
  isProcessing: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const ProcessingStatus: React.FC<ProcessingStatusProps> = ({ 
  status, 
  isProcessing, 
  error,
  onRetry 
}) => {
  const getStatusIcon = () => {
    if (error) {
      return <ErrorIcon color="error" />;
    }
    
    switch (status.status) {
      case 'uploaded':
        return <CloudUpload color="primary" />;
      case 'extracting':
        return <CircularProgress size={24} />;
      case 'processing':
        return <AutoAwesome color="primary" sx={{ animation: 'pulse 2s infinite' }} />;
      case 'completed':
        return <Check color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <CircularProgress size={24} />;
    }
  };

  const getStatusMessage = () => {
    if (error) {
      return error;
    }
    
    switch (status.status) {
      case 'uploaded':
        return 'Document uploaded successfully';
      case 'extracting':
        return 'Extracting text from document...';
      case 'processing':
        return 'AI is redacting personal information...';
      case 'completed':
        return 'Document processing completed!';
      case 'failed':
        return status.error_message || 'Processing failed';
      default:
        return 'Processing...';
    }
  };

  const getProgressColor = () => {
    if (error || status.status === 'failed') {
      return 'error';
    }
    if (status.status === 'completed') {
      return 'success';
    }
    return 'primary';
  };

  const formatEstimatedTime = (seconds?: number): string => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s remaining`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s remaining`;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {getStatusIcon()}
        <Typography variant="h6" sx={{ ml: 1, flex: 1 }}>
          Processing Document
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <LinearProgress 
          variant="determinate" 
          value={status.progress_percentage} 
          color={getProgressColor()}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {status.progress_percentage}% complete
          </Typography>
          {status.estimated_time_remaining && (
            <Typography variant="body2" color="text.secondary">
              {formatEstimatedTime(status.estimated_time_remaining)}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {getStatusMessage()}
      </Typography>

      {(error || status.status === 'failed') && onRetry && (
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<Refresh />}
              onClick={onRetry}
              disabled={isProcessing}
            >
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {status.error_message || error || 'Processing failed'}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'text.disabled' }}>
        <span>Started: {new Date(status.created_at).toLocaleTimeString()}</span>
        <span>Updated: {new Date(status.updated_at).toLocaleTimeString()}</span>
      </Box>
    </Box>
  );
};

export default ProcessingStatus;